import { useState, useEffect } from 'react';
import { Calendar, Clock, ArrowRight, ExternalLink, Rss } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';

const SUBSTACK_URL = 'https://churnlogix.substack.com';

interface SubstackArticle {
  title: string;
  link: string;
  pubDate: string;
  description: string;
  thumbnail?: string;
}

export function BlogListPage() {
  const [articles, setArticles] = useState<SubstackArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    async function fetchSubstackArticles() {
      setIsLoading(true);
      setFetchError(false);

      try {
        const rssUrl = `${SUBSTACK_URL}/feed`;
        const apiUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`;

        const response = await fetch(apiUrl);
        const data = await response.json();

        if (data.status === 'ok' && data.items) {
          const parsedArticles: SubstackArticle[] = data.items.map((item: any) => ({
            title: item.title,
            link: item.link,
            pubDate: item.pubDate,
            description: stripHtml(item.description).substring(0, 200) + '...',
            thumbnail: item.thumbnail || item.enclosure?.link,
          }));
          setArticles(parsedArticles);
        } else {
          setFetchError(true);
        }
      } catch (error) {
        console.error('Error fetching articles:', error);
        setFetchError(true);
      } finally {
        setIsLoading(false);
      }
    }

    fetchSubstackArticles();
  }, []);

  function stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  return (
    <div className="min-h-screen bg-white dark:bg-obsidian">
      {/* Hero */}
      <section className="section-padding bg-slate-50 dark:bg-obsidian">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-black text-obsidian dark:text-signal">
              Retention Intelligence
            </h1>
            <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
              Every issue covers one piece of the retention system. Signals, risk categories,
              intervention playbooks, or renewal architecture. Written for operators.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={`${SUBSTACK_URL}/subscribe`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="lg" leftIcon={<Rss className="w-5 h-5" />}>
                  Subscribe free
                </Button>
              </a>
              <a
                href={SUBSTACK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber hover:underline flex items-center gap-1"
              >
                View archive
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="section-padding bg-white dark:bg-obsidian border-t border-slate-200 dark:border-slate-800">
        <div className="container-custom">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-amber border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-slate-500">
                Loading latest articles...
              </p>
            </div>
          ) : fetchError || articles.length === 0 ? (
            <div className="max-w-2xl mx-auto text-center py-12">
              <Card variant="bordered" padding="lg">
                <CardContent>
                  <Rss className="w-16 h-16 text-amber mx-auto mb-4" />
                  <h2 className="text-2xl font-black text-obsidian dark:text-signal">
                    Read the latest issues
                  </h2>
                  <p className="mt-4 text-slate-600 dark:text-slate-400">
                    We publish our newsletter on Substack. Subscribe to get notified
                    when we publish new content about retention systems and churn prevention.
                  </p>
                  <div className="mt-6">
                    <a
                      href={SUBSTACK_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button size="lg" leftIcon={<ExternalLink className="w-5 h-5" />}>
                        View on Substack
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-black text-obsidian dark:text-signal">
                  Latest issues
                </h2>
                <a
                  href={SUBSTACK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber hover:underline flex items-center gap-1 text-sm"
                >
                  View all
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {articles.map((article, index) => (
                  <a
                    key={article.link}
                    href={article.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group"
                  >
                    <Card
                      variant="bordered"
                      padding="none"
                      hover
                      className="overflow-hidden h-full animate-in"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      {article.thumbnail ? (
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={article.thumbnail}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      ) : (
                        <div className="aspect-video bg-gradient-to-br from-amber/20 to-slate-100 dark:from-amber/10 dark:to-obsidian flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-white/50 dark:bg-obsidian/50 flex items-center justify-center">
                            <span className="text-2xl font-black text-amber">
                              {article.title.charAt(0)}
                            </span>
                          </div>
                        </div>
                      )}

                      <CardContent className="p-6">
                        <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(article.pubDate), 'MMM d, yyyy')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            5 min read
                          </span>
                        </div>

                        <h3 className="text-xl font-semibold text-obsidian dark:text-signal mb-2 group-hover:text-amber transition-colors">
                          {article.title}
                        </h3>

                        <p className="text-slate-600 dark:text-slate-500 text-sm line-clamp-3">
                          {article.description}
                        </p>

                        <span className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-amber">
                          Read issue
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </CardContent>
                    </Card>
                  </a>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="section-padding bg-slate-100 dark:bg-[#0F1014]">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-black text-obsidian dark:text-signal">
              Get retention intelligence in your inbox
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-400">
              Subscribe for practical frameworks on reducing churn and building
              retention systems. No spam, just insights.
            </p>
            <div className="mt-6">
              <a
                href={`${SUBSTACK_URL}/subscribe`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="lg" leftIcon={<Rss className="w-5 h-5" />}>
                  Subscribe free
                </Button>
              </a>
            </div>
            <p className="mt-4 text-sm text-slate-500">
              Free newsletter. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BlogListPage;
